import { exerciseNow, setExerciseTimeout, clearExerciseTimeout } from "../exercises/pause-clock";
import { ReadingPreviewNotice, useReadingPreview } from "./ReadingPreview";
import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useEffect, useMemo, useState } from "react";
import type { SilbentrennungExerciseConfig, SilbentrennungRunSettings } from "@calega/content";
import { formatSilbentrennung, type SilbentrennungItemAnalysis, type SilbentrennungRun } from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { useRealtime } from "../realtime/RealtimeContext";
import { ExerciseMenu } from "./ExerciseMenu";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";
import { ExerciseDurationSelect } from "./ExerciseSettings";
import { abortExerciseRun, submitExerciseAnswer } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";

type Stage = "vowels" | "splits";
type Phase = "running" | "feedback" | "complete";
type Answer = { itemId: string; stage: Stage; analysis: SilbentrennungItemAnalysis };
type State = {
  result?: { score: number; maxScore: number };
  runId: string;
  run: SilbentrennungRun;
  index: number;
  stage: Stage;
  phase: Phase;
  endsAt: number | null;
  vowelSelections: number[];
  splits: number[];
  answers: Answer[];
};

export function toggleSilbentrennungPosition(values: number[], position: number) {
  return values.includes(position) ? values.filter((value) => value !== position) : [...values, position].sort((left, right) => left - right);
}

export function Silbentrennung({ account, config, pageId }: { account: Account; config: SilbentrennungExerciseConfig; pageId: string }) {
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const follower = Boolean(realtime.session && !facilitator);
  const canControl = !realtime.session || facilitator;
  const canInteract = !realtime.session || facilitator || follower;
  const [mode, setMode] = useState<SilbentrennungRunSettings["mode"]>(config.defaultMode);
  const [count, setCount] = useState(config.defaultCount);
  const [durationSeconds, setDurationSeconds] = useState(config.defaultDurationSeconds);
  const [syllableCounts, setSyllableCounts] = useState<SilbentrennungRunSettings["syllableCounts"]>(config.defaultSyllableCounts);
  const [vowelMarking, setVowelMarking] = useState(config.defaultVowelMarking);
  const [graphemeHighlight, setGraphemeHighlight] = useState(config.defaultGraphemeHighlight);
  const [exercise, setExercise] = useState<State | null>(null);
  useLearningDisplayItem(exercise?.runId, exercise?.index ?? 0, Boolean(exercise && exercise.phase !== "complete"));
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [, setClockTick] = useState(0);

  const availableCounts = useMemo(() => [2, 3, 4].filter((count) => config.words.some((word) => Math.min(4, word.syllables.length) === count)) as Array<2 | 3 | 4>, [config.words]);
  const publish = (state: State | null) => realtime.publishExercise({ exerciseType: "silbentrennung", pageId, state });
  const settings = (): SilbentrennungRunSettings => ({ mode, count, durationSeconds, syllableCounts, vowelMarking, graphemeHighlight });
  const preview = useReadingPreview({ contentPageId: pageId, settings: settings() }, account.actingAs?.id ?? account.id, !exercise && canControl && syllableCounts.length > 0);

  useEffect(() => {
    const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; state?: State | null } | null;
    if (synced?.exerciseType === "silbentrennung" && synced.pageId === pageId) setExercise(synced.state ?? null);
  }, [pageId, realtime.state.exercise]);

  useEffect(() => {
    if (!exercise?.endsAt || exercise.phase === "complete") return;
    const timer = window.setInterval(() => setClockTick((value) => value + 1), 250);
    return () => window.clearInterval(timer);
  }, [exercise?.endsAt, exercise?.phase]);

  useEffect(() => {
    if (busy || !exercise?.endsAt || exercise.phase === "complete" || !canControl) return;
    const timer = setExerciseTimeout(() => finish().catch((error) => setMessage(error.message)), Math.max(0, exercise.endsAt - exerciseNow(realtime.serverNow)) + 20);
    return () => clearExerciseTimeout(timer);
  }, [canControl, exercise?.endsAt, exercise?.index, exercise?.phase, busy]);

  useEffect(() => {
    const answer = exercise?.answers.at(-1);
    if (!exercise || exercise.phase !== "feedback" || answer?.stage !== "splits" || !answer.analysis.correct || !canControl) return;
    const timer = setExerciseTimeout(() => next().catch((error) => setMessage(error.message)), 600);
    return () => clearExerciseTimeout(timer);
  }, [canControl, exercise?.answers, exercise?.phase, busy]);

  async function start(nextSettings = settings()) {
    if (busy || !canControl || nextSettings.syllableCounts.length < 1) return;
    setBusy(true);
    try {
      const result = await startExerciseRun<{ runId: string; exercise: SilbentrennungRun }>({ contentPageId: pageId, settings: nextSettings });
      const next: State = { runId: result.runId, run: result.exercise, index: 0, stage: nextSettings.vowelMarking ? "vowels" : "splits", phase: "running", endsAt: nextSettings.mode === "time" ? exerciseNow(realtime.serverNow) + nextSettings.durationSeconds * 1_000 : null, vowelSelections: [], splits: [], answers: [] };
      setExercise(next);
      setMessage("");
      publish(next);
    } finally { setBusy(false); }
  }

  async function abortCurrentRun() {
    if (exercise && exercise.phase !== "complete") await abortExerciseRun(exercise.runId);
  }

  async function backToSettings() {
    if (!exercise || !canControl || busy) return;
    setBusy(true);
    try {
      await abortCurrentRun();
      setMode(exercise.run.settings.mode);
      setCount(exercise.run.settings.count);
      setDurationSeconds(exercise.run.settings.durationSeconds);
      setSyllableCounts(exercise.run.settings.syllableCounts);
      setVowelMarking(exercise.run.settings.vowelMarking);
      setGraphemeHighlight(exercise.run.settings.graphemeHighlight);
      setExercise(null);
      setMessage("");
      publish(null);
    } finally { setBusy(false); }
  }

  async function remix() {
    if (!exercise || !canControl || busy) return;
    const nextSettings = exercise.run.settings;
    setBusy(true);
    try { await abortCurrentRun(); } finally { setBusy(false); }
    await start(nextSettings);
  }

  function update(next: State) {
    setExercise(next);
    publish(next);
  }

  function selectVowel(position: number) {
    if (!exercise || exercise.phase !== "running" || exercise.stage !== "vowels" || !canInteract) return;
    update({ ...exercise, vowelSelections: toggleSilbentrennungPosition(exercise.vowelSelections, position) });
  }

  function clearVowelSelections() {
    if (!exercise || exercise.phase !== "running" || exercise.stage !== "vowels" || !canInteract) return;
    update({ ...exercise, vowelSelections: [] });
  }

  function selectSplit(position: number) {
    if (!exercise || exercise.phase !== "running" || exercise.stage !== "splits" || !canInteract) return;
    update({ ...exercise, splits: toggleSilbentrennungPosition(exercise.splits, position) });
  }

  async function check() {
    if (!exercise || exercise.phase !== "running" || busy || !canInteract) return;
    const item = exercise.run.items[exercise.index];
    if (!item) return;
    setBusy(true);
    try {
      const result = await submitExerciseAnswer<{ analysis: SilbentrennungItemAnalysis }>(exercise.runId, { type: "silbentrennung", itemId: item.id, stage: exercise.stage, vowelSelections: exercise.vowelSelections, splits: exercise.splits });
      if (exercise.stage === "vowels" && result.analysis.vowelCorrect) {
        update({ ...exercise, stage: "splits", vowelSelections: [...exercise.vowelSelections], splits: [] });
        return;
      }
      update({ ...exercise, phase: "feedback", answers: [...exercise.answers, { itemId: item.id, stage: exercise.stage, analysis: result.analysis }] });
    } finally { setBusy(false); }
  }

  async function continueToSplits() {
    if (!exercise || exercise.phase !== "feedback" || exercise.stage !== "vowels" || !canControl) return;
    update({ ...exercise, phase: "running", stage: "splits", splits: [] });
  }

  async function next() {
    if (!exercise || exercise.phase !== "feedback" || !canControl || busy) return;
    if ((exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length) return finish();
    update({ ...exercise, index: exercise.index + 1, stage: exercise.run.settings.vowelMarking ? "vowels" : "splits", phase: "running", vowelSelections: [], splits: [] });
  }

  async function finish() {
    if (!exercise || exercise.phase === "complete" || !canControl || busy) return;
    await completion.complete(exercise.runId, (result) => {
      const next = { ...exercise, result, phase: "complete" as const };
      update(next);
      setMessage("");
    });
  }

  async function closeCompletedRun(repeat: boolean) {
    if (!exercise || exercise.phase !== "complete") return;
    if (repeat) await remix();
    else await backToSettings();
  }

  function toggleSyllableCount(value: 2 | 3 | 4) {
    setSyllableCounts((current) => current.includes(value) ? current.filter((count) => count !== value) : [...current, value].sort((left, right) => left - right) as Array<2 | 3 | 4>);
  }

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (follower && !exercise) return <ExerciseWaitingCard className="silbentrennung-exercise" />;

  if (!exercise) return <section className="silbentrennung-exercise">
    <p className="quiz-instructions learning-text">{config.instructions}</p>
    <div className="silbentrennung-settings">
      <fieldset><legend>Silbenanzahl</legend><div className="blitz-choice-row">{availableCounts.map((value) => <label className="checkbox-label" key={value}><input type="checkbox" disabled={!canControl || busy} checked={syllableCounts.includes(value)} onChange={() => toggleSyllableCount(value)} />{value === 4 ? "4+-silbig" : `${value}-silbig`}</label>)}</div></fieldset>
      <fieldset><legend>Durchlauf</legend><div className="blitz-choice-row"><label className="checkbox-label"><input type="radio" disabled={!canControl || busy} checked={mode === "count"} onChange={() => setMode("count")} />Feste Anzahl</label><label className="checkbox-label"><input type="radio" disabled={!canControl || busy} checked={mode === "time"} onChange={() => setMode("time")} />Auf Zeit</label></div></fieldset>
      {mode === "count" ? <label>Wortanzahl<select disabled={!canControl || busy} value={count} onChange={(event) => setCount(Number(event.target.value))}>{[5, 10, 15, 20].map((value) => <option key={value} value={value}>{value}</option>)}</select></label> : <ExerciseDurationSelect disabled={!canControl || busy} value={durationSeconds} onChange={setDurationSeconds} />}
      <fieldset><legend>Optionale Hilfen</legend><div className="blitz-choice-row"><label className="checkbox-label"><input type="checkbox" disabled={!canControl || busy} checked={vowelMarking} onChange={(event) => setVowelMarking(event.target.checked)} />Zuerst Vokale markieren</label><label className="checkbox-label"><input type="checkbox" disabled={!canControl || busy} checked={graphemeHighlight} onChange={(event) => setGraphemeHighlight(event.target.checked)} />Mehrgrafen hervorheben</label></div></fieldset>
    </div>
    <ReadingPreviewNotice preview={preview.preview} error={preview.error} />
    <button className="primary" disabled={busy || !canControl || syllableCounts.length < 1 || !preview.preview || preview.preview.eligible === 0} onClick={() => start().catch((error) => setMessage(error.message))}>Silbenübung starten</button>
    {!syllableCounts.length && <div className="alert info">Bitte mindestens eine Silbenanzahl auswählen.</div>}
    {message && <div className="alert info">{message}</div>}
  </section>;

  const item = exercise.run.items[exercise.index];
  const answer = exercise.answers.at(-1);
  const progress = exercise.run.settings.mode === "time" ? `${exercise.answers.filter(({ stage }) => stage === "splits").length} beantwortet · ${Math.max(0, Math.ceil(((exercise.endsAt ?? exerciseNow(realtime.serverNow)) - exerciseNow(realtime.serverNow)) / 1_000))} s` : `Wort ${Math.min(exercise.index + 1, exercise.run.items.length)} von ${exercise.run.items.length}`;
  const characters = item ? Array.from(item.surface) : [];
  const lockedPositions = new Set(item?.orthographicUnits.filter((unit) => unit.unitType === "locked_cluster").flatMap((unit) => Array.from({ length: unit.end - unit.start }, (_, offset) => unit.start + offset)) ?? []);
  const expectedVowels = new Set(item?.expectedVowelPositions ?? []);
  const score = exercise.answers.filter(({ stage, analysis }) => stage === "splits" && analysis.correct).length;
  const last = Boolean(exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length;

  return <section className="silbentrennung-exercise">
    {canControl && exercise.phase !== "complete" && <ExerciseMenu><button className="secondary small" disabled={busy} onClick={() => backToSettings().catch((error) => setMessage(error.message))}>← Zurück zu den Einstellungen</button><button className="secondary small" disabled={busy} onClick={() => remix().catch((error) => setMessage(error.message))}>↻ Neu mischen</button></ExerciseMenu>}
    <div className="exercise-progress">{progress}</div>
    {exercise.phase === "complete" ? <div className="reime-result"><strong>{exercise.result?.score ?? score} von {exercise.result?.maxScore ?? exercise.answers.filter(({ stage }) => stage === "splits").length} Wörtern richtig</strong><p>Der Durchlauf ist abgeschlossen.</p></div> : item && <>
      <div className="silbentrennung-card">
        <h2 className="learning-text">{exercise.stage === "vowels" ? "Markiere alle Vokale im Wort." : "Klicke zwischen zwei Buchstaben, um das Wort in Lesesilben zu trennen."}</h2>
        <div className={`silbentrennung-word ${exercise.phase === "feedback" ? "feedback" : ""}`} aria-label={`Wort ${item.surface}`}>
          {characters.map((character, index) => {
            const vowelSelected = exercise.vowelSelections.includes(index);
            return <span className="silbentrennung-character-group" key={`${character}-${index}`}>
              {index > 0 && <button type="button" className={`silbentrennung-boundary ${exercise.splits.includes(index) ? "selected" : ""} ${exercise.phase === "feedback" && item.expectedSplits.includes(index) ? "expected" : ""}`} disabled={exercise.stage !== "splits" || exercise.phase !== "running" || busy || !canInteract} onClick={() => selectSplit(index)} aria-label={`Trennstelle vor ${character}`}>{exercise.splits.includes(index) ? "|" : "·"}</button>}
              <button
                type="button"
                className={`silbentrennung-letter learning-text ${vowelSelected ? "selected" : ""} ${graphemeHighlight && lockedPositions.has(index) ? "locked" : ""} ${exercise.phase === "feedback" && exercise.stage === "vowels" && expectedVowels.has(index) ? "expected" : ""}`}
                disabled={exercise.stage !== "vowels" || exercise.phase !== "running" || busy || !canInteract}
                aria-pressed={vowelSelected}
                aria-label={vowelSelected ? `${character} ist markiert. Erneut antippen, um die Markierung zu entfernen.` : `${character} als Vokal markieren`}
                onClick={() => selectVowel(index)}
              >
                {character}
              </button>
            </span>;
          })}
        </div>
        {exercise.stage === "vowels" && exercise.phase === "running" && <p className="assignment-help">Tippe einen markierten Buchstaben erneut an, um die Markierung zu entfernen.</p>}
        {exercise.phase === "feedback" && answer?.stage === "splits" && !answer.analysis.splitsCorrect && <div className="silbentrennung-solution learning-text">Richtig getrennt: <strong>{formatSilbentrennung(item.surface, item.expectedSplits)}</strong></div>}
      </div>
      {exercise.phase === "feedback" && answer && <div className={`answer-feedback learning-text ${answer.analysis.correct ? "correct" : "wrong"}`}>{answer.stage === "vowels" ? "Noch nicht ganz. Die richtigen Vokale sind grün markiert." : answer.analysis.correct ? "Richtig getrennt." : "Noch nicht ganz. Die Lösung wird angezeigt."}</div>}
      <div className="silbentrennung-actions">
        {exercise.phase === "running" && exercise.stage === "vowels" && exercise.vowelSelections.length > 0 && <button className="secondary" disabled={busy || !canInteract} onClick={clearVowelSelections}>Markierungen zurücksetzen</button>}
        {exercise.phase === "running" && <button className="primary" disabled={busy || !canInteract} onClick={() => check().catch((error) => setMessage(error.message))}>Überprüfen</button>}
        {exercise.phase === "feedback" && answer?.stage === "vowels" && canControl && <button className="primary" disabled={busy} onClick={() => continueToSplits().catch((error) => setMessage(error.message))}>Weiter zum Trennen</button>}
        {exercise.phase === "feedback" && answer?.stage === "splits" && !answer.analysis.correct && canControl && <button className="primary" disabled={busy} onClick={() => next().catch((error) => setMessage(error.message))}>{last ? "Auswertung ansehen" : "Nächstes Wort"}</button>}
      </div>
    </>}
    {canControl && exercise.phase === "complete" && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}
    {exercise.phase === "complete" && <CompletedRunHistory key={exercise.runId} runId={exercise.runId} />}
    {message && <div className="alert info">{message}</div>}
  </section>;
}
